/* eslint-disable no-useless-escape */
import React, { useState } from "react";
import { Modal, Button } from "reactstrap";
import { withQuery } from "containers/withQuery";
import { compose, graphql } from "react-apollo";

import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import translateErrorMessage from "utils/translateErrorMessage";
import CostBox from "./CostBox.jsx";
import CONCRETE_FORMULA_QUERY from "./concreteFormulaQueries.js";
import { CONCRETE_FORMULAS_QUERY } from "../FamiliesAndFormulasQueries";
import {
  InfoContainer,
  CostBoxContainer,
  TableContainer,
} from "./ConcreteTracesModalStyle.jsx";
import { CREATE_CONCRETE_FORMULA_MUTATION } from "./concreteFormulaMutations.js";
import MaterialsTable from "./MaterialsTable.jsx";
import ConcreteFormulaHeader from "./ConcreteFormulaHeader.jsx";
import ConcreteFormulaBody from "./ConcreteFormulaBody.jsx";
import { UPDATE_REQUIRED_FIELDS, MATERIAL_REQUIRED_FIELDS, isBlank } from "./requiredFields";
import { calculateMortarFactor, calculateWcFactor } from "./calculations.js";
import getConcreteFamiliesOptions, { getConcreteFamilyName } from "./getConcreteFamiliesOptions";

const ConcreteFormulaCopyCreate = (props) => {
  const {
    isActive: initialIsActive,
    fcj: initialFcj,
    fck: initialFck,
    use: initialUse,
    wcRatio: initialWcRatio,
    fctmk: initialFctmk,
    mortarFactor: initialMortarFactor,
    observations: initialObservations,
    incorporatedAir: initialIncorporatedAir,
    consumption: initialConsumption,
    aggressivenessIndex: initialAggressivenessIndex,
    concreteFamily: initialConcreteFamily,
    margin: initialMargin,
    code: initialCode,
  } = props.concreteFormula;

  const [state, setState] = useState({
    code: `Cópia de - ${initialCode}`,
    materialsHasChanged: false,
    isEditable: true,
    shouldUpdate: true,
    isActive: initialIsActive,
    materials: [],
    margin: initialMargin || 0,
    use: initialUse,
    concreteFamily: {
      ...initialConcreteFamily,
      value: initialConcreteFamily.id,
      label: getConcreteFamilyName(initialConcreteFamily.name, initialConcreteFamily.slump),
    },
    fck: initialFck,
    fcj: initialFcj,
    fctmk: initialFctmk,
    consumption: initialConsumption,
    mortarFactor: initialMortarFactor,
    wcRatio: initialWcRatio,
    aggressivenessIndex: initialAggressivenessIndex,
    incorporatedAir: initialIncorporatedAir,
    observations: initialObservations,
    materialsSummary: null,
  });

  const onChangeActive = e => setState(prev => ({
    ...prev,
    isActive: e.state.value,
    shouldUpdate: false
  }));

  const onChangeData = (key, name) => setState(prev => ({
    ...prev,
    [key]: name,
    shouldUpdate: false
  }));

  const getTotalsSummary = (materials = []) => {
    if (!materials || materials.length === 0) return [];
  
    const isAggregate = (m) => {
      const t = m.name.materialType;
      return t === "SAND" || t === "GRAVEL";
    };
  
    const toNumber = (w) => {
      if (w == null) return 0;
      if (typeof w === "object" && w.label != null) return parseFloat(w.label);
      return parseFloat(w);
    };
  
    const toRho = (rho) => {
      if (!rho) return NaN;
      if (typeof rho === "object" && rho.label != null) return parseFloat(rho.label);
      return parseFloat(rho);
    };
  
    let totalWeight = 0;
    let totalVolume = 0;
    let totalAggregateWeight = 0;
  
    materials.forEach((m) => {
      const w = toNumber(m.weight);
      const rho = toRho(m.specificWeight);
  
      if (!isNaN(w)) {
        totalWeight += w;
        if (rho > 0 && !isNaN(rho)) {
          totalVolume += w / rho;
        }
      }
  
      if (isAggregate(m)) {
        totalAggregateWeight += isNaN(w) ? 0 : w;
      }
    });
  
    if (!(totalWeight > 0)) return [];
  
    const agg = totalAggregateWeight.toFixed(2);
    const load = totalWeight.toFixed(2);
    const volume = totalVolume.toFixed(2) / 1000;
  
    return [
      {
        id: "aggregateTotal",
        title: "Massa total de agregados",
        value: `${agg} Kg`,
      },
      {
        id: "loadTotal",
        title: "Massa total da fórmula",
        value: `${load} Kg`,
      },
      {
        id: "volumeTotal",
        title: "Volume total",
        value: `${volume} m³`,
      },
    ];
  };

  const onChangeMaterials = materials => {
    setState(prev => {
      const mortarFactor = prev.isEditable ? calculateMortarFactor(materials) : prev.mortarFactor;
      const wcRatio = prev.isEditable ? calculateWcFactor(materials) : prev.wcRatio;
      const materialsSummary = getTotalsSummary(materials);

      return {
        ...prev,
        materials,
        materialsHasChanged: true,
        shouldUpdate: false,
        mortarFactor,
        wcRatio,
        materialsSummary,
      };
    });
  };

  const formatToR$ByVolume = value => (value !== "" ? `R\$\ ${parseFloat(value).toFixed(2)}/m³` : "-");

  const isValid = (object, requiredFields) => {
    const { snackbarContext } = props;

    const missingFields = requiredFields.filter(field => {
      const value = object[field.key];

      if (field.select) {
        return !value || isBlank(value[field.select]);
      }

      return isBlank(value);
    });

    if (missingFields.length === 0) return true;

    const message = missingFields.length === 1
      ? `${missingFields[0].label} é obrigatório`
      : `${missingFields.map(({ label }) => label).join(", ")} são obrigatórios`;

    snackbarContext.setNotificationTimeOut(Color.danger, message);
    return false;
  };

  const onSaveClick = () => {
    const {
      code,
      isActive,
      use,
      fck,
      fcj,
      fctmk,
      aggressivenessIndex,
      incorporatedAir,
      observations,
      materials,
      margin,
      concreteFamily,
      materialsHasChanged,
    } = state;

    if (!isValid(state, UPDATE_REQUIRED_FIELDS)) {
      return;
    }

    if (materials.length === 0) {
      props.snackbarContext.setNotificationTimeOut(Color.danger, "É necessário informar pelo menos 1 material.");
      return;
    }

    const invalidMaterial = materials.find(material => !isValid(material, MATERIAL_REQUIRED_FIELDS));

    if (invalidMaterial) {
      return;
    }

    props.createConcreteFormula({
      variables: {
        params: {
          aggressivenessIndex: parseFloat(aggressivenessIndex),
          code,
          concreteFamilyId: concreteFamily.value,
          fcj: parseInt(fcj, 10),
          fck: parseInt(fck, 10),
          fctmk: parseFloat(fctmk),
          formulaMaterials: materials.map(material => ({
            inventoryItemId: material.name.value,
            proportion: parseFloat(material.proportion),
            order: material.id,
            weight: parseFloat(material.weight.label),
            specificWeight: parseFloat(material.specificWeight.label),
            supplierId: parseInt(material.supplier.value, 10)
          })),
          incorporatedAir: parseFloat(incorporatedAir),
          isActive,
          margin,
          observations,
          use,
        },
      },
      refetchQueries: [
        {
          query: CONCRETE_FORMULA_QUERY,
          variables: { id: props.id },
        },
        {
          query: CONCRETE_FORMULAS_QUERY,
          variables: { concreteFamilyIds: props.concreteFamilyIds }
        }
      ]
    })
      .then(() => {
        props.snackbarContext.setNotificationTimeOut(Color.success, "Traço criado com sucesso.");
        props.closeModalHandler();
      })
      .catch(error => {
        props.snackbarContext.setNotificationTimeOut(Color.danger, translateErrorMessage(error));
      });
  };

  const {
    code,
    isActive,
    use,
    concreteFamily,
    fck,
    fcj,
    fctmk,
    consumption,
    mortarFactor,
    wcRatio,
    aggressivenessIndex,
    incorporatedAir,
    margin,
    observations,
    isEditable,
    shouldUpdate,
    materialsSummary
  } = state;

  const { price } = props.concreteFormula;

  const concreteFamiliesOptions = getConcreteFamiliesOptions(props.concreteFamilies);

  const total = (margin !== "" ? parseInt(margin, 10) : 0) + parseInt(price || 0, 10);

  return (
    <Modal
      isOpen={props.modalOpen}
      toggle={props.closeModalHandler}
      size="lg"
      style={{ maxWidth: "1600px", width: "80%" }}
    >
      <ConcreteFormulaHeader
        onChangeActive={onChangeActive}
        closeModalHandler={props.closeModalHandler}
        isActive={isActive}
      />
      <ConcreteFormulaBody
        code={code}
        isEditable={isEditable}
        use={use}
        concreteFamily={concreteFamily}
        concreteFamiliesOptions={concreteFamiliesOptions}
        fck={fck}
        fcj={fcj}
        fctmk={fctmk}
        consumption={consumption}
        mortarFactor={mortarFactor}
        wcRatio={wcRatio}
        aggressivenessIndex={aggressivenessIndex}
        incorporatedAir={incorporatedAir}
        observations={observations}
        onChangeData={onChangeData}
        materialsSummary={materialsSummary}
      />
      <TableContainer className="modal-body" style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
        <MaterialsTable
          materials={props.concreteFormula.formulaMaterials}
          onChangeMaterials={onChangeMaterials}
          inventoryItems={props.inventoryItems}
          suppliers={props.suppliers}
          isEditable={isEditable}
          shouldUpdate={shouldUpdate}
        />
        <InfoContainer>
          <CostBoxContainer>
            <CostBox
              title="Custo total do traço"
              value={price}
              formatter={formatToR$ByVolume}
            />
            <CostBox
              isEditable={isEditable}
              keyValue="margin"
              onChange={onChangeData}
              title="Margem"
              value={margin !== 0 ? margin : 0}
              formatter={formatToR$ByVolume}
            />
            <CostBox
              title="Preço"
              value={total}
              formatter={formatToR$ByVolume}
            />
          </CostBoxContainer>
        </InfoContainer>
      </TableContainer>
      <div className="modal-footer">
        <Button
          style={{ width: "100%", padding: "17px 16px", margin: 0 }}
          color="info"
          data-dismiss="modal"
          type="button"
          onClick={onSaveClick}
        >
          Criar Traço
        </Button>
      </div>
    </Modal>
  );
};

export default compose(
  withSnackbarConsumer,
  graphql(CREATE_CONCRETE_FORMULA_MUTATION, { name: "createConcreteFormula", }),
  withQuery(({ id }) => ({
    noLoader: true,
    query: CONCRETE_FORMULA_QUERY,
    fetchPolicy: "cache-and-network",
    variables: {
      id,
    },
  })),
)(ConcreteFormulaCopyCreate);
